import { useEffect } from 'react'
import { useIsMobile } from './breakpoints'

/*
  Provides a fixed pixel value CSS variable to components which need to use 100vh.
  100vh cannot directly be used, because mobile browsers (especially chrome) show/hide bottom controls
  based on scrolling up or down. When this happens, the absolute number of 100vh also gets bigger
  or smaller, which changes height of all 100vh containers, changing the overall scrollstory
  container height. Due to the absolute scroll position staying the same, we get scrolling jumps.
  => This hook makes sure we have a CSS variable which does not change when the controls are shown/hidden.
*/

const VIEWPORT_HEIGHT_VAR_NAME = '--scrollstory-viewport-height'
const VIEWPORT_HEIGHT_DELTA_VAR_NAME = '--scrollstory-viewport-height-delta'

export const VIEWPORT_HEIGHT = `var(${VIEWPORT_HEIGHT_VAR_NAME})`

// Stores delta between VIEWPORT_HEIGHT and currently visible viewport (without browser controls).
// Can be used for paddings to avoid slipping under controls
export const VIEWPORT_HEIGHT_DELTA = `var(${VIEWPORT_HEIGHT_DELTA_VAR_NAME})`

export const useStableViewportHeight = (): void => {
  const isMobile = useIsMobile()

  useEffect(() => {
    let resizeTimeout: number | undefined
    let currentHeight = getStableViewportHeight()
    saveCssVariable(VIEWPORT_HEIGHT_VAR_NAME, currentHeight)
    saveCssVariable(VIEWPORT_HEIGHT_DELTA_VAR_NAME, 0)

    const handleResize = () => {
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout)
      }

      resizeTimeout = window.setTimeout(() => {
        const newHeight = getStableViewportHeight()

        if (isMobile) {
          if (newHeight > currentHeight) {
            currentHeight = newHeight
            saveCssVariable(VIEWPORT_HEIGHT_VAR_NAME, currentHeight)
            saveCssVariable(VIEWPORT_HEIGHT_DELTA_VAR_NAME, 0)
          } else {
            const delta = currentHeight - newHeight
            saveCssVariable(VIEWPORT_HEIGHT_DELTA_VAR_NAME, delta)
          }
        } else {
          if (newHeight !== currentHeight) {
            currentHeight = newHeight
            saveCssVariable(VIEWPORT_HEIGHT_VAR_NAME, currentHeight)
          }
        }
      }, 10)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout)
      }
    }
  }, [isMobile])
}

const getStableViewportHeight = () => {
  const viewportHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight
  )
  return viewportHeight
}

const saveCssVariable = (variableName: string, height: number) => {
  document.documentElement.style.setProperty(variableName, `${height}px`)
}
